import React from "react";
import { createPortal } from "react-dom";
import "./GeotabRealTimelineMarks.css";

import { HoverCard } from "./ResultsPrimitives.jsx";
import { chipClass, fmtDelta, fmtDuration, fmtTime, lineColor } from "./geotab/geotabFormat.js";
import { checkHoverLine } from "../utils/dispatchGeotabCheck.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";

// Réel Geotab posé sur la piste d'un camion (voir utils/geotabRealTimeline.js) : pastille d'avancement au coin
// du segment, trait sous ses deux parties (sur place, puis route vers l'étape suivante) coloré par l'écart.
const PROGRESS = {
  done: { color: "#15803d", text: "✓", label: "Fait" },
  now: { color: "#1d4ed8", text: "●", label: "Sur place, pas encore terminé" },
  skip: { color: "#b91c1c", text: "✕", label: "Pas vu : une étape suivante est déjà faite" },
};
const EMPTY_LINE = "#cbd5e1";
const shown = (part) => part && part.tone !== "ok" && part.tone !== "empty";

function DurationRow({ label, part }) {
  if (!part) return null;
  return (
    <>
      <dt className="whitespace-nowrap font-semibold text-slate-500">{label}</dt>
      <dd className="m-0 tabular-nums font-semibold text-slate-800">
        {`${fmtDuration(part.plan)} prévu · `}
        {part.real == null ? <span className="text-slate-500">{part.why || "réel inconnu"}</span> : (
          <>
            {`${fmtDuration(part.real)} réel `}
            <span className={`ml-1 inline-block rounded-[3px] px-1 text-[0.68rem] font-bold ${chipClass(part.tone)}`}>{fmtDelta(part.gap)}</span>
          </>
        )}
      </dd>
    </>
  );
}

function MarkHover({ mark, links, children }) {
  return (
    <HoverCard
      title={mark.title}
      content={(
        <div className="grid gap-1.5">
          <dl className="m-0 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-0.5">
            <dt className="whitespace-nowrap font-semibold text-slate-500">Geotab</dt>
            <dd className="m-0 font-semibold text-slate-800">
              {mark.progress === "now" && mark.since != null ? `Arrivé à ${fmtTime(mark.since)}, pas encore terminé : ✓ quand le camion repart` : PROGRESS[mark.progress]?.label}
            </dd>
            <DurationRow label="Sur place" part={mark.dwell} />
            <DurationRow label={`Route vers ${mark.route?.to || ""}`} part={mark.route} />
          </dl>
          {links}
        </div>
      )}
      accentColor="#14532d"
      delay={200}
      interactive={Boolean(links)}
      maxWidth={420}
      placement="bottom"
      referenceAsChild
    >
      {children}
    </HoverCard>
  );
}

// « 2/3 » sous le camion : arrêts terminés sur les arrêts prévus (sans l'inspection ni la veille), puis le ● de
// l'arrêt en cours et les ✕ des arrêts sautés, comme sur les segments. Arrêts vus par Geotab hors du Dispatch d'une
// journée en cours ou passée (gap) : un chiffre rouge « ⚠ 3 » = arrêts sautés ou pas vus + arrêts hors du Dispatch.
export function GeotabRealProgressChip({ summary = null, gap = null }) {
  const cursorRef = React.useRef(null);
  const handRef = React.useRef(null);
  const hideCursor = React.useCallback(() => {
    cursorRef.current?.removeAttribute("data-geotab-cursor");
    handRef.current?.removeAttribute("data-geotab-cursor");
  }, []);
  const moveCursor = (event) => {
    if (event.pointerType !== "mouse" || !handRef.current) return;
    handRef.current.style.left = `${event.clientX}px`;
    handRef.current.style.top = `${event.clientY}px`;
    handRef.current.setAttribute("data-geotab-cursor", "ready");
    event.currentTarget.setAttribute("data-geotab-cursor", "ready");
  };
  React.useEffect(() => {
    if (!gap?.count) return undefined;
    window.addEventListener("blur", hideCursor);
    document.addEventListener("visibilitychange", hideCursor);
    document.addEventListener("scroll", hideCursor, true);
    return () => {
      window.removeEventListener("blur", hideCursor);
      document.removeEventListener("visibilitychange", hideCursor);
      document.removeEventListener("scroll", hideCursor, true);
    };
  }, [gap?.count, hideCursor]);
  if (!summary?.total && !gap) return null;
  const color = summary?.skipped ? PROGRESS.skip.color : PROGRESS.done.color;
  const plural = (count, word) => `${count} ${word}${count > 1 ? "s" : ""}`;
  const label = summary?.total ? [
    `Geotab : ${plural(summary.done, "arrêt")} terminé${summary.done > 1 ? "s" : ""} sur ${summary.total}`,
    summary.ongoing ? `${summary.ongoing} en cours` : "",
    summary.skipped ? `${summary.skipped} pas vu${summary.skipped > 1 ? "s" : ""}` : "",
  ].filter(Boolean).join(", ") : "Geotab";
  const counter = summary?.total ? (
    <>
      {`${summary.done}/${summary.total}`}
      {summary.ongoing ? <span style={{ color: PROGRESS.now.color }}>●</span> : null}
      {summary.done === summary.total ? " ✓" : ""}
    </>
  ) : null;
  const className = "ml-auto inline-flex h-4 min-w-[30px] shrink-0 items-center justify-center gap-0.5 rounded-[4px] border px-1 text-[0.58rem] font-bold leading-none tabular-nums";
  // Le contrôle compte déjà les sites prévus pas vus ; pendant sa lecture, le ✕ du Réel reste visible.
  const diffs = gap ? gap.count : summary?.skipped || 0;
  if (!diffs) return <span data-geotab-real-summary="true" className={`${className} bg-white`} style={{ borderColor: color, color }} aria-label={label}>{counter}</span>;
  if (!gap) return <span data-geotab-real-summary="true" className={`${className} border-[#b91c1c] bg-white text-[#b91c1c]`} aria-label={label} title={label}>⚠ {diffs}</span>;
  const open = (event) => { event.preventDefault(); event.stopPropagation(); hideCursor(); gap.open(); };
  return (
    <>
    <HoverCard title={`Geotab · ${plural(diffs, "différence")}`} placement="bottom-start" maxWidth={400} referenceAsChild
      content={(
        <div className="grid gap-1.5 text-[0.82rem] leading-5 text-slate-700">
          {gap.missed?.length ? <div>Prévu, pas vu par Geotab : {gap.missed.join(", ")}</div> : null}
          {gap.items.map((item, index) => {
            const { where, kind } = checkHoverLine(item);
            return <div key={index}>{where.charAt(0).toUpperCase()}{where.slice(1)}<div className="text-xs text-slate-500">{kind}</div></div>;
          })}
          <div className="border-t border-slate-200 pt-1 text-xs text-slate-500">Clic : voir l’écart sur la ligne du camion.</div>
        </div>
      )}>
      <span role="button" tabIndex={0} data-geotab-real-summary="true" data-geotab-gap={gap.seen ? "seen" : "new"}
        ref={cursorRef}
        className={`geotab-gap-click-cursor pointer-events-auto ml-auto inline-flex h-4 min-w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-[4px] border border-[#b91c1c] px-1 text-[0.58rem] font-bold leading-none tabular-nums focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${gap.seen ? "bg-white text-[#b91c1c] hover:bg-slate-50" : "bg-[#b91c1c] text-white hover:bg-[#991b1b]"}`}
        aria-label={`${label}, ${plural(diffs, "différence")} avec Geotab : voir`}
        onPointerEnter={moveCursor} onPointerMove={moveCursor}
        onPointerLeave={hideCursor} onBlur={hideCursor}
        onPointerDown={(event) => event.stopPropagation()} onClick={open}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") open(event); }}>
        ⚠ {diffs}
      </span>
    </HoverCard>
    {typeof document !== "undefined" ? createPortal(
      <span ref={handRef} className="geotab-click-hand" aria-hidden="true" />,
      getReactPortalRoot(),
    ) : null}
    </>
  );
}

const badgeClass ="pointer-events-auto absolute z-40 flex cursor-help items-center whitespace-nowrap rounded-[4px] border-white font-bold leading-none shadow-[0_1px_2px_rgba(0,0,0,0.25)]";

export default function GeotabRealTimelineMarks({ marks = [], showProgress = false, showDurations = false, links = null }) {
  if (!showProgress && !showDurations) return null;
  return marks.map((mark) => {
    const right = mark.leftPercent + mark.widthPercent;
    const split = mark.dwell ? mark.leftPercent + (mark.widthPercent * mark.servicePercent) / 100 : mark.leftPercent;
    const progress = PROGRESS[mark.progress];
    const parts = showDurations && mark.progress !== "skip"
      ? [mark.dwell && [mark.leftPercent, split, mark.progress === "now" ? { tone: "now" } : mark.dwell, ""], [split, right, mark.route, "↝ "]].filter(Boolean)
      : [];
    return (
      <React.Fragment key={`geotab-real:${mark.key}`}>
        {showProgress && mark.progress === "skip" ? (
          <span aria-hidden="true" data-geotab-real-skip="true" className="pointer-events-none absolute top-0.5 z-30 h-7.5 rounded-[4px] border-2 border-dashed border-[#b91c1c]"
            style={{ left: `${mark.leftPercent}%`, width: `${mark.widthPercent}%` }} />
        ) : null}
        {showProgress && !showDurations && mark.progress !== "skip" ? (
          <span aria-hidden="true" data-geotab-real-line="true" className="pointer-events-none absolute top-7 z-30 h-1 rounded-b-[3px]"
            style={{ left: `calc(${mark.leftPercent}% + 2px)`, width: `calc(${mark.widthPercent}% - 4px)`, background: progress.color }} />
        ) : null}
        {parts.map(([from, to, part, prefix]) => (
          <React.Fragment key={prefix || "dwell"}>
            <span aria-hidden="true" data-geotab-real-line="true" className="pointer-events-none absolute top-7 z-30 h-1"
              style={{ left: `calc(${from}% + 1px)`, width: `calc(${to - from}% - 2px)`, background: part?.tone === "now" ? PROGRESS.now.color : part && part.tone !== "empty" ? lineColor(part.tone) : EMPTY_LINE }} />
            {/* Chaque écart est centré sous sa portion du segment : sur place, puis route. */}
            {shown(part) && part.tone !== "now" ? (
              <MarkHover mark={mark} links={links?.(mark)}>
                <span role="img" aria-label={`${prefix ? "Route" : "Sur place"} ${fmtDelta(part.gap)} par rapport au prévu`}
                  className={`${badgeClass} top-8.5 h-3.75 border-[1.5px] px-0.75 text-[0.62rem] -translate-x-1/2 ${chipClass(part.tone)}`}
                  style={{ left: `${(from + to) / 2}%` }}
                  data-geotab-real-gap={prefix ? "route" : "dwell"}>
                  {`${prefix}${fmtDelta(part.gap).replace(" min", "")}`}
                </span>
              </MarkHover>
            ) : null}
          </React.Fragment>
        ))}
        {showProgress ? (
          <MarkHover mark={mark} links={links?.(mark)}>
            <span role="img" aria-label={`${mark.title} : ${progress.label}`} data-geotab-real-progress={mark.progress}
              className={`${badgeClass} -top-1 h-3 min-w-3 border px-px text-[0.5rem] -translate-x-[85%] justify-center text-white${mark.progress === "now" ? " motion-safe:animate-pulse" : ""}`} style={{ left: `${right}%`, background: progress.color }}>
              {progress.text}
            </span>
          </MarkHover>
        ) : null}
      </React.Fragment>
    );
  });
}
